/**
 * AddonManager - Sistema de addons de productos para Shopify
 * VISO Project
 * 
 * Principios aplicados:
 * - Single Responsibility: Este módulo solo gestiona addons
 * - DRY: Funciones reutilizables para localStorage, cart API, sync
 * - Open/Closed: Configuración externa via data-attributes
 */

// Fallback para ShowSecond si no está definida (evitar ReferenceError)
if (typeof window.ShowSecond === 'undefined') {
  window.ShowSecond = function() {
    // Función placeholder - el modal se maneja desde AddonManager
    console.log('AddonManager: ShowSecond called');
  };
}
const AddonManager = (function() {
  'use strict';

  // ==========================================
  // CONFIGURACIÓN
  // ==========================================
  const CONFIG = Object.assign({
    STORAGE_KEY: 'viso_product_addons',
    ADDON_HANDLE: 'pillow-insert',
    ADDON_VARIANT_ID: null,
    SELECTORS: {
      checkbox: '.addon-checkbox',
      container: '.addon-option',
      cartItem: '.cart-item, tr.cart-item, [data-cart-item]',
      cartItemTitle: '.cart-item__title, .cart-item-title, td.title',
      addonLabel: '.addon-injected-label',
      productSelect: '#product-select, [name="id"]',
      productForm: '#product-actions, form[action="/cart/add"]',
      priceDisplay: '#product-price, .product-price'
    }
  }, window.VISO_ADDON_CONFIG || {});

  // ==========================================
  // STORAGE - Gestión de localStorage
  // ==========================================
  const Storage = {
    get() {
      try {
        const data = localStorage.getItem(CONFIG.STORAGE_KEY);
        return data ? JSON.parse(data) : {};
      } catch (e) {
        console.error('AddonManager: Error reading localStorage', e);
        return {};
      }
    },

    set(data) {
      try {
        localStorage.setItem(CONFIG.STORAGE_KEY, JSON.stringify(data));
      } catch (e) {
        console.error('AddonManager: Error writing localStorage', e);
      }
    },

    addAddon(productVariantId, addonVariantId) {
      const addons = this.get();
      addons[productVariantId] = addonVariantId;
      this.set(addons);
    },

    removeAddon(productVariantId) {
      const addons = this.get();
      delete addons[productVariantId];
      this.set(addons);
    },

    hasAddon(productVariantId) {
      const addons = this.get();
      return !!addons[productVariantId];
    },

    getAddonVariantId(productVariantId) {
      const addons = this.get();
      return addons[productVariantId] || null;
    }
  };

  // ==========================================
  // CART API - Interacción con carrito Shopify
  // ==========================================
  const CartAPI = {
    async getCart() {
      const response = await fetch('/cart.js');
      return response.json();
    },

    async addItems(items) {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });
      return response.json();
    },

    async updateQuantities(updates) {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });
      return response.json();
    },

    async updateByKey(key, quantity) {
      // Actualizar item específico por su line item key
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      return response.json();
    },

    async removeItem(variantId) {
      return this.updateQuantities({ [variantId]: 0 });
    },

    // Buscar item en carrito por variant ID y propiedad específica
    findItemWithProperty(cart, variantId, propertyKey, propertyValue) {
      return cart.items.find(item => 
        item.variant_id === variantId && 
        item.properties && 
        item.properties[propertyKey] === propertyValue
      );
    },

    // Buscar item en carrito solo por variant ID
    findItem(cart, variantId) {
      return cart.items.find(item => item.variant_id === variantId);
    }
  };

  // ==========================================
  // PRODUCT PAGE - Lógica página de producto
  // ==========================================
  const ProductPage = {
    originalPrice: null,
    
    init() {
      const checkboxes = document.querySelectorAll(CONFIG.SELECTORS.checkbox);
      if (!checkboxes.length) return;

      // Guardar precio original
      const priceEl = document.querySelector(CONFIG.SELECTORS.priceDisplay);
      if (priceEl) {
        this.originalPrice = priceEl.innerHTML;
      }

      checkboxes.forEach(checkbox => {
        this.bindCheckbox(checkbox);
        this.restoreState(checkbox);
      });

      this.bindVariantChange();
      this.interceptAddToCart();
    },

    // Obtener variant ID actual del producto
    getCurrentVariantId() {
      const select = document.querySelector(CONFIG.SELECTORS.productSelect);
      return select ? select.value : null;
    },

    // Actualizar data-attribute cuando cambia variante
    bindVariantChange() {
      const select = document.querySelector(CONFIG.SELECTORS.productSelect);
      if (!select) return;

      select.addEventListener('change', () => {
        const container = document.querySelector(CONFIG.SELECTORS.container);
        if (container) {
          const oldVariantId = container.dataset.productVariantId;
          const newVariantId = select.value;
          
          // Migrar addon al nuevo variant si estaba checked
          const checkbox = container.querySelector(CONFIG.SELECTORS.checkbox);
          if (checkbox && checkbox.checked) {
            Storage.removeAddon(oldVariantId);
            Storage.addAddon(newVariantId, container.dataset.addonVariantId);
          }
          
          // Actualizar data-attribute
          container.dataset.productVariantId = newVariantId;
        }
      });
    },

    bindCheckbox(checkbox) {
      checkbox.addEventListener('change', (e) => {
        const container = e.target.closest(CONFIG.SELECTORS.container);
        const productVariantId = this.getCurrentVariantId() || container.dataset.productVariantId;
        const addonVariantId = container.dataset.addonVariantId;

        // Actualizar data-attribute con variante actual
        container.dataset.productVariantId = productVariantId;

        if (e.target.checked) {
          Storage.addAddon(productVariantId, addonVariantId);
          this.updatePriceDisplay(container, true);
        } else {
          Storage.removeAddon(productVariantId);
          this.updatePriceDisplay(container, false);
        }
      });
    },

    restoreState(checkbox) {
      const container = checkbox.closest(CONFIG.SELECTORS.container);
      const productVariantId = this.getCurrentVariantId() || container.dataset.productVariantId;
      
      // Actualizar data-attribute
      container.dataset.productVariantId = productVariantId;
      
      if (Storage.hasAddon(productVariantId)) {
        checkbox.checked = true;
        this.updatePriceDisplay(container, true);
      }
    },

    updatePriceDisplay(container, hasAddon) {
      const priceElement = document.querySelector(CONFIG.SELECTORS.priceDisplay);
      if (!priceElement || !this.originalPrice) return;
      
      if (hasAddon) {
        const addonPrice = parseInt(container.dataset.addonPrice, 10);
        const addonTitle = container.dataset.addonTitle || 'Relleno';
        
        // Añadir indicador visual del addon al precio
        priceElement.innerHTML = this.originalPrice + 
          ` <span class="addon-price-indicator">+ ${addonTitle}</span>`;
      } else {
        priceElement.innerHTML = this.originalPrice;
      }
    },

    interceptAddToCart() {
      const form = document.querySelector(CONFIG.SELECTORS.productForm);
      const button = document.querySelector('#add-to-cart');
      if (!form || !button) return;

      let isProcessing = false;

      // Interceptar click en el botón (antes que el onclick inline)
      button.addEventListener('click', async (e) => {
        const checkbox = document.querySelector(CONFIG.SELECTORS.checkbox);
        if (!checkbox || !checkbox.checked) return; // Dejar que funcione normal

        // Prevenir todo
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        // Evitar doble procesamiento
        if (isProcessing) return;
        isProcessing = true;

        try {
          const container = checkbox.closest(CONFIG.SELECTORS.container);
          const productVariantId = parseInt(this.getCurrentVariantId() || container.dataset.productVariantId, 10);
          const addonVariantId = parseInt(container.dataset.addonVariantId, 10);
          
          // Obtener info del producto para las propiedades
          const productTitle = document.querySelector('.product-title, h1')?.textContent?.trim() || 'Product';
          const variantSelect = document.querySelector(CONFIG.SELECTORS.productSelect);
          let variantTitle = variantSelect?.options[variantSelect.selectedIndex]?.text || '';
          
          // Filtrar "Default Title" ya que no aporta información útil
          if (variantTitle.toUpperCase() === 'DEFAULT TITLE') {
            variantTitle = '';
          }
          
          const productInfo = variantTitle ? `${productTitle} - ${variantTitle}` : productTitle;

          // Obtener carrito actual para verificar si ya existen los items
          const cart = await CartAPI.getCart();
          
          // Buscar si ya existe este producto CON la propiedad "Pillow Insert: Included"
          const existingPillow = CartAPI.findItemWithProperty(cart, productVariantId, 'Pillow Insert', 'Included');
          
          // Buscar si ya existe el insert CON la propiedad "For Product" de este producto
          const existingInsert = CartAPI.findItemWithProperty(cart, addonVariantId, 'For Product', productInfo);

          console.log('AddonManager: Existing pillow with insert?', existingPillow ? 'Yes' : 'No');
          console.log('AddonManager: Existing insert for this product?', existingInsert ? 'Yes' : 'No');

          if (existingPillow && existingInsert) {
            // Ambos existen - incrementar cantidades
            console.log('AddonManager: Incrementing existing items');
            await CartAPI.updateByKey(existingPillow.key, existingPillow.quantity + 1);
            await CartAPI.updateByKey(existingInsert.key, existingInsert.quantity + 1);
          } else {
            // No existen - añadir nuevos
            console.log('AddonManager: Adding new items with properties');
            const items = [
              { 
                id: productVariantId, 
                quantity: 1,
                properties: {
                  "Pillow Insert": "Included"
                }
              },
              { 
                id: addonVariantId, 
                quantity: 1,
                properties: {
                  "For Product": productInfo
                }
              }
            ];
            await CartAPI.addItems(items);
          }
          
          Storage.addAddon(productVariantId, addonVariantId);
          
          // Trigger evento o modal de éxito
          this.showAddedToCart();
        } catch (error) {
          console.error('AddonManager: Error adding to cart', error);
          // Fallback: redirigir al carrito igualmente
          window.location.href = '/cart';
        } finally {
          isProcessing = false;
        }
      }, true); // Capture phase para ejecutar antes que el onclick inline

      // También interceptar el form submit como backup
      form.addEventListener('submit', (e) => {
        const checkbox = document.querySelector(CONFIG.SELECTORS.checkbox);
        if (checkbox && checkbox.checked) {
          e.preventDefault();
          e.stopPropagation();
          // El click handler ya manejó todo
        }
      }, true);
    },

    showAddedToCart() {
      // Activar modal existente o redirigir
      const modal = document.querySelector('#add-to-cart-modal');
      if (modal) {
        modal.classList.add('is-active');
        // Poblar modal con info del producto
        this.populateModal(modal);
      } else {
        window.location.href = '/cart';
      }
    },

    populateModal(modal) {
      // Intentar poblar el modal con la info del producto añadido
      const productTitle = document.querySelector('.product-title');
      const productImage = document.querySelector('.product-gallery-slider img');
      
      const modalTitle = modal.querySelector('#modal-product-title');
      const modalImage = modal.querySelector('#modal-product-image');
      
      if (modalTitle && productTitle) {
        modalTitle.textContent = productTitle.textContent;
      }
      if (modalImage && productImage) {
        modalImage.src = productImage.src;
      }
    }
  };

  // ==========================================
  // CART PAGE - Lógica página de carrito
  // ==========================================
  const CartPage = {
    addonVariantId: null,
    isSyncing: false,

    async init() {
      console.log('AddonManager: CartPage init');
      
      // Obtener ID del addon desde CONFIG o hacer fetch
      if (CONFIG.ADDON_VARIANT_ID) {
        this.addonVariantId = CONFIG.ADDON_VARIANT_ID;
      } else {
        await this.fetchAddonVariantId();
      }
      
      // Ejecutar sincronización inmediata al cargar
      await this.syncQuantities();
      
      this.hideAddonItems();
      // Labels now handled server-side in cart.liquid
      this.bindQuantityChanges();
      this.bindRemoveLinks();
    },

    async fetchAddonVariantId() {
      try {
        const response = await fetch(`/products/${CONFIG.ADDON_HANDLE}.js`);
        const product = await response.json();
        this.addonVariantId = product.variants[0].id;
        console.log('AddonManager: Addon variant ID:', this.addonVariantId);
      } catch (e) {
        console.error('AddonManager: Could not fetch addon product', e);
      }
    },

    hideAddonItems() {
      if (!this.addonVariantId) return;

      document.querySelectorAll('.cart-item, .item').forEach(item => {
        const variantId = item.dataset.variantId;
        const handle = item.dataset.handle;
        
        if (parseInt(variantId, 10) === this.addonVariantId || handle === CONFIG.ADDON_HANDLE) {
          item.classList.add('addon-item-hidden');
          item.style.display = 'none';
        }
      });
    },

    async injectAddonLabels() {
      try {
        const cart = await CartAPI.getCart();
        
        // Encontrar todos los pillows que tienen "Pillow Insert: Included"
        const pillowsWithInsert = cart.items.filter(item => 
          item.properties && item.properties['Pillow Insert'] === 'Included'
        );

        console.log('AddonManager: Pillows with insert to label:', pillowsWithInsert.length);

        pillowsWithInsert.forEach(pillow => {
          // Buscar el elemento en el DOM por su line item key
          const cartItem = document.querySelector(
            `.cart-item[data-line-item-key="${pillow.key}"], .item[data-line-item-key="${pillow.key}"]`
          );
          
          if (cartItem && !cartItem.querySelector('.addon-injected-label')) {
            const label = document.createElement('div');
            label.className = 'addon-injected-label';
            label.innerHTML = `<span class="addon-check">✓</span> With pillow insert`;
            
            // Buscar elemento para insertar después (título del producto)
            const titleElement = cartItem.querySelector('.product-title, .cart-item__title, .main-information');
            if (titleElement) {
              titleElement.insertAdjacentElement('afterend', label);
            } else {
              // Fallback: insertar al principio del item
              cartItem.insertAdjacentElement('afterbegin', label);
            }
          }
        });
      } catch (error) {
        console.error('AddonManager: Error injecting labels', error);
      }
    },

    bindQuantityChanges() {
      // Bindear botones de cantidad + y -
      document.querySelectorAll('.quantity-button').forEach(button => {
        button.addEventListener('click', () => {
          // Pequeño delay para permitir que el valor se actualice
          setTimeout(() => this.syncQuantities(), 100);
        });
      });

      // Bindear inputs de cantidad (por si hay)
      document.querySelectorAll('.quantity-field, input[name="updates[]"]').forEach(input => {
        input.addEventListener('change', () => this.syncQuantities());
      });

      // Observer para cambios dinámicos
      const cartForm = document.querySelector('#cart-wrapper, form[action="/cart"]');
      if (cartForm) {
        const observer = new MutationObserver(() => {
          if (!this.isSyncing) {
            this.syncQuantities();
          }
        });
        observer.observe(cartForm, { childList: true, subtree: true, attributes: true });
      }
    },

    bindRemoveLinks() {
      // Bindear links de "REMOVE" para limpiar localStorage cuando se elimina un producto
      document.querySelectorAll('.remove a, a[href*="/cart/change"]').forEach(link => {
        link.addEventListener('click', () => {
          const cartItem = link.closest('.cart-item, .item');
          if (cartItem) {
            const variantId = cartItem.dataset.variantId;
            if (Storage.hasAddon(variantId)) {
              Storage.removeAddon(variantId);
              console.log('AddonManager: Removed addon association for variant', variantId);
            }
          }
        });
      });
    },

    async syncQuantities() {
      if (this.isSyncing) return;
      this.isSyncing = true;
      
      // Añadir clase para ocultar checkout mientras se sincroniza
      document.body.classList.add('addon-syncing');
      
      try {
        const cart = await CartAPI.getCart();
        const updates = {};

        console.log('AddonManager: Syncing. Cart items:', cart.items.length);

        // Encontrar todos los pillows que tienen "Pillow Insert: Included"
        const pillowsWithInsert = cart.items.filter(item => 
          item.properties && item.properties['Pillow Insert'] === 'Included'
        );

        // Encontrar todos los inserts (items del producto addon)
        const inserts = cart.items.filter(item => 
          item.handle === CONFIG.ADDON_HANDLE ||
          item.product_handle === CONFIG.ADDON_HANDLE ||
          item.variant_id === this.addonVariantId
        );

        console.log('AddonManager: Pillows with insert:', pillowsWithInsert.length);
        console.log('AddonManager: Inserts in cart:', inserts.length);

        // Para cada pillow con insert, buscar su insert correspondiente
        for (const pillow of pillowsWithInsert) {
          // Construir el identificador del producto (debe coincidir con "For Product")
          // No incluir "Default Title" ya que no aporta información útil
          const variantTitle = pillow.variant_title && pillow.variant_title.toUpperCase() !== 'DEFAULT TITLE' ? pillow.variant_title : '';
          const productInfo = variantTitle ? pillow.product_title + ' - ' + variantTitle : pillow.product_title;
          
          // Buscar el insert que tenga "For Product" igual a este pillow
          const matchingInsert = inserts.find(insert => 
            insert.properties && insert.properties['For Product'] === productInfo
          );

          console.log(`AddonManager: Pillow "${productInfo}" (qty: ${pillow.quantity}), Insert:`, matchingInsert ? `found (qty: ${matchingInsert.quantity})` : 'NOT FOUND');

          if (matchingInsert) {
            // Sincronizar cantidades si no coinciden
            if (pillow.quantity !== matchingInsert.quantity) {
              console.log(`AddonManager: Syncing insert quantity from ${matchingInsert.quantity} to ${pillow.quantity}`);
              updates[matchingInsert.key] = pillow.quantity;
            }
          }
        }

        // Buscar inserts huérfanos (inserts sin pillow correspondiente)
        for (const insert of inserts) {
          const forProduct = insert.properties && insert.properties['For Product'];
          if (forProduct) {
            // Verificar si existe un pillow con ese nombre
            const hasPillow = pillowsWithInsert.some(pillow => {
              const variantTitle = pillow.variant_title && pillow.variant_title.toUpperCase() !== 'DEFAULT TITLE' ? pillow.variant_title : '';
              const productInfo = variantTitle ? pillow.product_title + ' - ' + variantTitle : pillow.product_title;
              return productInfo === forProduct;
            });

            if (!hasPillow) {
              console.log(`AddonManager: Orphan insert found for "${forProduct}", removing`);
              updates[insert.key] = 0;
            }
          } else {
            // Insert sin propiedad "For Product" - es huérfano
            console.log('AddonManager: Insert without "For Product" property, removing');
            updates[insert.key] = 0;
          }
        }

        if (Object.keys(updates).length > 0) {
          console.log('AddonManager: Updating cart with:', updates);
          // Usar change.js con keys
          for (const [key, quantity] of Object.entries(updates)) {
            await CartAPI.updateByKey(key, quantity);
          }
          window.location.reload();
        }
      } catch (error) {
        console.error('AddonManager: Sync error', error);
      } finally {
        this.isSyncing = false;
        document.body.classList.remove('addon-syncing');
      }
    }
  };

  // ==========================================
  // INICIALIZACIÓN
  // ==========================================
  function init() {
    const isProductPage = document.querySelector(CONFIG.SELECTORS.container);
    const isCartPage = window.location.pathname === '/cart';

    if (isProductPage) {
      ProductPage.init();
    }

    if (isCartPage) {
      CartPage.init();
    }
  }

  // Auto-init cuando DOM esté listo
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // API pública
  return {
    Storage,
    CartAPI,
    ProductPage,
    CartPage,
    init
  };

})();
